import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import RecipeCard from '../components/RecipeCard';
import { RevealItem } from '../components/Reveal';
import SectionBg from '../components/SectionBg';
import { recipes, categories, occasions } from '../data/recipes';

const gridStagger = {
  initial: 'hidden',
  whileInView: 'show',
  viewport: { once: true, amount: 0.05 },
  variants: { show: { transition: { staggerChildren: 0.05 } } },
};

const sorts = {
  popular: { label: 'Popularité', fn: (a, b) => b.votes - a.votes },
  rating: { label: 'Mieux notées', fn: (a, b) => b.rating - a.rating },
  cheap: { label: 'Prix croissant', fn: (a, b) => a.estimated_cost - b.estimated_cost },
  fast: { label: 'Plus rapides', fn: (a, b) => a.prep_time - b.prep_time },
};

const RecipesPage = () => {
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState(params.get('category') || '');
  const [occasion, setOccasion] = useState('');
  const [sort, setSort] = useState('popular');

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    return recipes
      .filter((r) => {
        if (q && !`${r.name} ${r.description}`.toLowerCase().includes(q)) return false;
        if (category && r.category !== category) return false;
        if (occasion && !(r.occasions || []).includes(occasion)) return false;
        return true;
      })
      .sort(sorts[sort].fn);
  }, [query, category, occasion, sort]);

  const pickCategory = (key) => {
    setCategory(key);
    const next = new URLSearchParams(params);
    if (key) next.set('category', key);
    else next.delete('category');
    setParams(next, { replace: true });
  };

  return (
    <>
      <Banner>
        <SectionBg tone="chocolate" variant={1} />
        <Inner>
          <Kicker>Le catalogue</Kicker>
          <h1>Toutes les recettes de la joie</h1>
          <p>Plus de 1 200 idées gourmandes à filtrer selon ton envie, ton occasion et ton budget.</p>
        </Inner>
      </Banner>

      <Inner>
        <Toolbar>
          <Search>
            <i className="ti ti-search" aria-hidden="true" />
            <input
              type="text"
              placeholder="Rechercher une recette…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </Search>
          <SortSelect value={sort} onChange={(e) => setSort(e.target.value)}>
            {Object.entries(sorts).map(([k, v]) => (
              <option key={k} value={k}>Trier : {v.label}</option>
            ))}
          </SortSelect>
        </Toolbar>

        <Chips>
          <Chip $active={!category} onClick={() => pickCategory('')}>Toutes</Chip>
          {categories.map((c) => (
            <Chip key={c.key} $active={category === c.key} onClick={() => pickCategory(c.key)}>
              {c.label}
            </Chip>
          ))}
          <Spacer />
          <OccSelect value={occasion} onChange={(e) => setOccasion(e.target.value)}>
            <option value="">Toutes les occasions</option>
            {occasions.map((o) => (
              <option key={o} value={o}>{o.charAt(0).toUpperCase() + o.slice(1)}</option>
            ))}
          </OccSelect>
        </Chips>

        <Count>{results.length} recette{results.length > 1 ? 's' : ''}</Count>

        {results.length === 0 ? (
          <Empty>
            <i className="ti ti-mood-empty" aria-hidden="true" />
            <p>Aucune recette ne correspond. Essaie d'élargir tes filtres.</p>
          </Empty>
        ) : (
          <Grid as={motion.div} key={`${category}-${occasion}-${sort}-${query}`} {...gridStagger}>
            {results.map((r) => (
              <RevealItem key={r.id}><RecipeCard recipe={r} /></RevealItem>
            ))}
          </Grid>
        )}
      </Inner>
    </>
  );
};

const Banner = styled.section`
  background: ${({ theme }) => theme.colors.secondary};
  padding: 40px 0 36px;
  position: relative;
  isolation: isolate;
  h1 { font-size: clamp(2rem, 4vw, 2.8rem); color: ${({ theme }) => theme.colors.chocolate}; margin-bottom: 8px; }
  p { color: ${({ theme }) => theme.colors.chocolate}; opacity: 0.85; margin: 0; max-width: 60ch; }
`;
const Inner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
`;
const Kicker = styled.span`
  display: block; color: ${({ theme }) => theme.colors.primary};
  font-weight: 800; font-size: 0.85rem; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 6px;
`;
const Toolbar = styled.div`
  display: flex; gap: 14px; margin: 26px 0 16px; flex-wrap: wrap;
`;
const Search = styled.label`
  flex: 1; min-width: 240px;
  display: flex; align-items: center; gap: 10px;
  background: #fff; border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.pill}; padding: 0 18px;
  i { color: ${({ theme }) => theme.colors.secondaryDark}; font-size: 1.2rem; }
  input { flex: 1; border: none; outline: none; padding: 14px 0; font-size: 1rem; font-family: inherit; background: transparent; }
`;
const baseSelect = `
  border: 1px solid #F0E6D2; border-radius: 999px; background: #fff;
  padding: 0 16px; height: 50px; font-size: 0.95rem; font-weight: 700; cursor: pointer;
  font-family: inherit; color: #4A2600;
`;
const SortSelect = styled.select`${baseSelect}`;
const OccSelect = styled.select`${baseSelect} height: 40px;`;
const Chips = styled.div`
  display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-bottom: 18px;
`;
const Chip = styled.button`
  border: 2px solid ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.border)};
  background: ${({ $active, theme }) => ($active ? theme.colors.primary : '#fff')};
  color: ${({ $active, theme }) => ($active ? '#fff' : theme.colors.chocolate)};
  font-weight: 800; font-size: 0.9rem; padding: 8px 16px; border-radius: 999px; cursor: pointer;
  text-transform: capitalize; transition: all .15s ease;
  &:hover { border-color: ${({ theme }) => theme.colors.primary}; }
`;
const Spacer = styled.div`flex: 1; min-width: 12px;`;
const Count = styled.div`
  color: ${({ theme }) => theme.colors.textSecondary}; font-weight: 700; margin-bottom: 16px;
`;
const Grid = styled.div`
  display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 26px;
  padding-bottom: 16px;
`;
const Empty = styled.div`
  text-align: center; padding: 60px 20px; color: ${({ theme }) => theme.colors.textSecondary};
  i { font-size: 2.6rem; color: ${({ theme }) => theme.colors.secondaryDark}; display: block; margin-bottom: 10px; }
`;

export default RecipesPage;
